import { t } from "@lingui/macro";
import { RouteProp, useRoute } from "@react-navigation/native";
import * as Sentry from "@sentry/react-native";
import { FlashList, FlashListRef } from "@shopify/flash-list";
import graphql from "babel-plugin-relay/macro";
import { format, parse } from "date-fns";
import { useAtom, useAtomValue } from "jotai";
import _ from "lodash";
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import {
  AppState,
  FlatList,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  ViewToken,
} from "react-native";
import { SharedValue } from "react-native-reanimated";
import { PreloadedQuery, usePreloadedQuery } from "react-relay";

import { CourseNote } from "@whoosh/design-system/src/apps/PatronApp/screens/Agenda/CourseNote";
import { WeatherConditionProps } from "@whoosh/design-system/src/components/display/WeatherCondition";
import {
  Box,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { useBreakpointValue } from "@whoosh/design-system/src/hooks/useBreakpointValue";
import { getClampedScrollOffsetY } from "@whoosh/design-system/src/patterns/patronApp/MobileHeader/MobileHeader";

import { getFacilityMemberPermissions } from "../../../../../hooks/getFacilityMemberPermissions";
import { ClubStackNavigatorParamList } from "../../../../../navigators/ClubNavigator";
import { useFlags } from "../../../../../utils/launchDarkly";
import {
  AgendaMemberNote,
  agendaMemberNotes_atom,
} from "../../../../MemberNotes/atoms";
import {
  teeSheetGroupSizeFilterAtom,
  teeSheetHoleFilterAtom,
  teeSheetListFilterAtom,
  teeSheetTimesAtom,
} from "../../../atoms";
import { useAgendaTeeSheetSlotsChangedSubscription } from "../hooks/useAgendaTeeSheetSlotsChangedSubscription";
import { useGetCanClientFulFillRequests } from "../hooks/useGetCanClientFulFillRequests";
import { useScrollToTimeSlotHour } from "../hooks/useScrollToTimeSlotHour";
import useTimeSlotListSubscription from "../hooks/useTimeSlotListSubscription";
import { timeSlotListInternalReducer } from "../reducers/timeSlotListInternalReducer";
import { getTeeSheetComponents } from "../utils/getTeeSheetComponents";
import {
  getNextClientRestrictionLiftAtMs,
  hasClientRestrictionRefreshTimeElapsed,
  hasClientExpiredRestrictions,
} from "../utils/hasClientExpiredRestrictions";
import { scheduleClientRestrictionRefresh } from "../utils/scheduleClientRestrictionRefresh";
import { TimeSlotSkeletonRow } from "./TimeSlotSkeletonRow";
import { WaitlistCta } from "./WaitlistCta/WaitlistCta";
import { TimeSlotListInternalQuery } from "./__generated__/TimeSlotListInternalQuery.graphql";

export const TimeSlotListInternal = ({
  queryRef,
  serverTimeOffsetMs,
  date,
  handleRowPress,
  onJoinableRowPress,
  currentSlotId,
  courseIds,
  setIsScrolled,
  scrollY,
  handleRequestedRowPress,
  membershipTypeId,
  isCustomer,
  isExpirationRefreshLoading,
  isCurrentQueryDataReady,
  onRestrictionRefreshDue,
  onRestrictionSubscriptionReceived,
  onTeeSheetDataLoaded,
}: {
  queryRef: PreloadedQuery<TimeSlotListInternalQuery>;
  serverTimeOffsetMs: number | null;
  date: string;
  handleRowPress: (slotId: string) => void;
  onJoinableRowPress: (slotId: string) => void;
  currentSlotId?: string | null;
  courseIds: string[];
  setIsScrolled: (val: boolean) => void;
  scrollY?: SharedValue<number>;
  handleRequestedRowPress: (queueSlotId: string, time: string) => void;
  membershipTypeId: string;
  isCustomer?: boolean;
  isExpirationRefreshLoading?: boolean;
  isCurrentQueryDataReady?: boolean;
  onRestrictionRefreshDue?: () => void;
  onRestrictionSubscriptionReceived?: () => void;
  onTeeSheetDataLoaded?: (hasExpiredRestrictions: boolean) => void;
}) => {
  const isMobileScreen = useBreakpointValue({
    base: true,
    xs: true,
    sm: true,
    md: true,
    agendaSplit: false,
    eventCardColumn: false,
    lg: false,
    xl: false,
  });

  const flashListRef = useRef<FlashListRef<any>>(null);
  const flatListRef = useRef<FlatList<any>>(null);
  const filter = useAtomValue(teeSheetListFilterAtom);
  const viewFilter = isCustomer ? "Available" : filter;
  const holeCount = useAtomValue(teeSheetHoleFilterAtom);
  const groupSize = useAtomValue(teeSheetGroupSizeFilterAtom);
  const teeSheetTimes = useAtomValue(teeSheetTimesAtom);

  const [state, dispatch] = useReducer(timeSlotListInternalReducer, {});

  const [memberNote, setMemberNote] = useAtom(agendaMemberNotes_atom);

  const data = usePreloadedQuery(TimeSlotListQueryString, queryRef);
  const nextClientRestrictionLiftAtMsRef = useRef<number | null>(null);

  useEffect(() => {
    onTeeSheetDataLoaded?.(
      hasClientExpiredRestrictions(data, serverTimeOffsetMs)
    );
  }, [data, onTeeSheetDataLoaded, serverTimeOffsetMs]);

  useEffect(() => {
    const hasExpiredRestrictions = hasClientExpiredRestrictions(
      data,
      serverTimeOffsetMs
    );
    const nextLiftAtMs = getNextClientRestrictionLiftAtMs(
      data,
      serverTimeOffsetMs
    );
    nextClientRestrictionLiftAtMsRef.current = nextLiftAtMs;

    return scheduleClientRestrictionRefresh({
      hasExpiredRestrictions,
      nextLiftAtMs,
      onRestrictionRefreshDue,
    });
  }, [data, onRestrictionRefreshDue, serverTimeOffsetMs]);

  useEffect(() => {
    const subscription = AppState.addEventListener("change", (nextAppState) => {
      const hasScheduledRefreshElapsed = hasClientRestrictionRefreshTimeElapsed(
        nextClientRestrictionLiftAtMsRef.current
      );

      if (
        nextAppState === "active" &&
        (hasClientExpiredRestrictions(data, serverTimeOffsetMs) ||
          hasScheduledRefreshElapsed)
      ) {
        onRestrictionRefreshDue?.();
      }
    });

    return () => {
      subscription.remove();
    };
  }, [data, onRestrictionRefreshDue, serverTimeOffsetMs]);

  const agenda = data.node?.agendas?.edges
    ? data.node.agendas.edges[0]?.node
    : null;

  const facilityPermissions = useMemo(
    () =>
      getFacilityMemberPermissions({
        fragmentRef: data.node?.__typename === "Facility" ? data.node : null,
      }),
    [data.node]
  );

  const { waitlistRestrictions, hideUnavailableTimes, requestConfigs } =
    useFlags();

  const clientCanFulfillRequests = useGetCanClientFulFillRequests(data.node);

  useEffect(() => {
    if (agenda?.memberNote) {
      const mn = { ...agenda.memberNote, date: "" };
      try {
        const date = parse(agenda?.date || "", "yyyy-MM-dd", new Date());
        mn.date = format(date, "MMM do");
      } catch (e) {
        Sentry.captureException(e);
      }
      setMemberNote(mn as AgendaMemberNote);
    } else {
      setMemberNote(null);
    }
  }, [setMemberNote, data, agenda?.memberNote, agenda?.date]);

  const supportsPublic = data?.node?.club?.supportsPublic ?? false;

  const [teeSheetComponents, teeSheetData] = useMemo(() => {
    return getTeeSheetComponents(
      data,
      handleRowPress,
      viewFilter,
      courseIds,
      holeCount,
      (id: string, time: string) => handleRequestedRowPress(id, time),
      onJoinableRowPress,
      groupSize,
      currentSlotId,
      (data.node?.courses?.count ?? 0) > 1,
      teeSheetTimes,
      clientCanFulfillRequests,
      supportsPublic,
      hideUnavailableTimes,
      serverTimeOffsetMs,
      !!requestConfigs,
      facilityPermissions
    );
  }, [
    data,
    handleRowPress,
    viewFilter,
    courseIds,
    holeCount,
    currentSlotId,
    onJoinableRowPress,
    handleRequestedRowPress,
    groupSize,
    teeSheetTimes,
    hideUnavailableTimes,
    clientCanFulfillRequests,
    supportsPublic,
    serverTimeOffsetMs,
    facilityPermissions,
    requestConfigs,
  ]);

  useEffect(() => {
    Sentry.addBreadcrumb({
      category: "tee_sheet.load",
      level: "info",
      message: t`Time slot list data resolved`,
      data: {
        agendaDate: agenda?.date ?? null,
        courseCount: courseIds.length,
        date,
        hasExpiredRestrictions: hasClientExpiredRestrictions(
          data,
          serverTimeOffsetMs
        ),
        platform: Platform.OS,
        rowCount: teeSheetData.length,
        visibleComponentCount: teeSheetComponents.length,
      },
    });
  }, [
    agenda?.date,
    courseIds.length,
    data,
    date,
    serverTimeOffsetMs,
    teeSheetComponents.length,
    teeSheetData.length,
  ]);

  const route = useRoute<RouteProp<ClubStackNavigatorParamList, "Agenda">>();

  useScrollToTimeSlotHour({
    startHourParam: route.params?.startHour,
    teeSheetComponents,
    flatListRef,
    flashListRef,
  });

  /**
   * Whenever an item scrolls off screen, we get its index and check to see
   * if we should change the pinned weather.
   */
  const [curIndex, setCurIndex] = useState(0);

  useEffect(() => {
    let matchingWeatherData: WeatherConditionProps | null = teeSheetData.reduce(
      (acc: WeatherConditionProps | null, cur, i) => {
        if (i <= curIndex && cur.type === "Weather") {
          return cur.data;
        }
        return acc;
      },
      null
    );

    if (!_.isEqual(matchingWeatherData, state.pinnedWeatherCondition)) {
      dispatch({ type: "updatePinnedCondition", payload: matchingWeatherData });
    }
  }, [curIndex, state.pinnedWeatherCondition, teeSheetData]);

  const onViewableItemsChanged = useCallback(
    ({
      viewableItems,
    }: {
      viewableItems: Array<ViewToken>;
      changed: Array<ViewToken>;
    }) => {
      const indexes = viewableItems.map((i) => i.index);
      const firstVisibleIndex = indexes[0] ?? 0;
      setCurIndex(firstVisibleIndex);
    },
    []
  );

  const handleScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const clampedOffsetY = getClampedScrollOffsetY({
        offsetY: event.nativeEvent.contentOffset.y,
        contentHeight: event.nativeEvent.contentSize?.height,
        viewportHeight: event.nativeEvent.layoutMeasurement?.height,
      });

      setIsScrolled(clampedOffsetY > 5);
      if (scrollY) {
        scrollY.value = clampedOffsetY;
      }
    },
    [scrollY, setIsScrolled]
  );

  useTimeSlotListSubscription({
    agendaId: agenda?.id,
    isCustomer,
  });
  useAgendaTeeSheetSlotsChangedSubscription({
    agendaId: agenda?.id || "",
    courseIds,
    date,
    membershipTypeId,
    isCustomer,
    requestConfigs: !!requestConfigs,
    onNext: onRestrictionSubscriptionReceived,
  });

  const firstTime =
    teeSheetComponents.length > 0 && teeSheetComponents[0].startAt
      ? teeSheetComponents[0].startAt
      : data.node?.agendas?.edges?.[0]?.node?.timeSlots?.edges?.[0]?.node?.time;

  const hasWaitlistRestrictions = agenda?.waitlistRestrictions?.edges?.length;

  const shouldShowJoinWaitlistOnEmptyAgenda = !!(
    waitlistRestrictions &&
    hasWaitlistRestrictions &&
    agenda?.queueSlot?.id &&
    firstTime
  );
  const closedRequestConfigMessage =
    requestConfigs && agenda?.configuredRequestRestrictions?.edges
      ? agenda.configuredRequestRestrictions.edges.find((edge) => {
          const requestClosesMs = edge?.requestCloses
            ? Date.parse(edge.requestCloses)
            : null;

          return (
            !edge?.liftAt &&
            requestClosesMs !== null &&
            !Number.isNaN(requestClosesMs) &&
            serverTimeOffsetMs !== null &&
            requestClosesMs < Date.now() + serverTimeOffsetMs &&
            !!edge?.node?.requestConfig?.closedRequestMessage
          );
        })?.node?.requestConfig?.closedRequestMessage ?? null
      : null;

  const closedRequestConfigMessageComponent = closedRequestConfigMessage ? (
    <Box alignItems="center" justifyContent="center" bg="grayscale-0" py={25}>
      {teeSheetComponents.length === 0 && (
        <Text
          accessibilityRole="header"
          variant="mainTitle3"
          maxFontSizeMultiplier={2}
          mb={4}
        >
          {t`Sorry!`}
        </Text>
      )}
      <Text>{closedRequestConfigMessage}</Text>
    </Box>
  ) : null;

  if (isExpirationRefreshLoading || !isCurrentQueryDataReady) {
    return <TimeSlotSkeletonRow />;
  }

  return (
    <>
      <VStack flex={1} width="100%">
        {teeSheetComponents.length === 0 ? (
          shouldShowJoinWaitlistOnEmptyAgenda ? (
            <Box
              px={{ base: 4, agendaSplit: 0 }}
              maxWidth={{ base: "100%", md: 720 }}
              width={"100%"}
              marginTop={10}
              style={{ marginHorizontal: "auto" }}
              mb={4}
            >
              <WaitlistCta
                queueSlotId={agenda?.queueSlot?.id ?? ""}
                time={firstTime || ""}
                clientCanFulfillRequests={clientCanFulfillRequests}
                handleRequestedRowPress={handleRequestedRowPress}
              />
            </Box>
          ) : (
            closedRequestConfigMessageComponent ?? (
              <Box
                alignItems="center"
                justifyContent="center"
                bg="grayscale-0"
                py={25}
              >
                <Text
                  accessibilityRole="header"
                  variant="mainTitle3"
                  maxFontSizeMultiplier={2}
                  mb={4}
                >
                  {t`Sorry!`}
                </Text>
                <Text>
                  {t`Looks like there aren't any times available on this day.`}
                </Text>
              </Box>
            )
          )
        ) : (
          closedRequestConfigMessageComponent
        )}
        <Box flexDirection="row" alignItems="flex-start" height="100%" flex={1}>
          <Box flex={1} flexGrow={1} height="100%">
            {teeSheetComponents.length > 0 &&
              (Platform.OS === "web" ? (
                <FlatList
                  onScroll={handleScroll}
                  scrollEventThrottle={20}
                  bounces={false}
                  ref={flatListRef}
                  data={teeSheetComponents}
                  showsVerticalScrollIndicator={false}
                  onScrollToIndexFailed={() => {}}
                  onViewableItemsChanged={onViewableItemsChanged}
                  ListHeaderComponent={
                    memberNote ? (
                      <Box
                        marginBottom={2}
                        justifyContent={"center"}
                        alignItems={"center"}
                        marginTop={!isMobileScreen ? 5 : 0}
                      >
                        <Box
                          maxWidth={isMobileScreen ? "100%" : 720}
                          width={"100%"}
                        >
                          <CourseNote
                            header={memberNote.header}
                            body={memberNote.body}
                            variant={isMobileScreen ? "underlay" : "inline"}
                            priority={memberNote.status}
                          />
                        </Box>
                      </Box>
                    ) : (
                      <Box height={28} mb={2}></Box>
                    )
                  }
                  keyExtractor={(item) => item.key}
                  ListFooterComponent={() => <Box height={96} />}
                  renderItem={({ item }) => (
                    <Box
                      px={{ base: 4, agendaSplit: 0 }}
                      maxWidth={{ base: "100%", md: 720 }}
                      width={"100%"}
                      style={{ marginHorizontal: "auto" }}
                    >
                      {item.component}
                    </Box>
                  )}
                />
              ) : (
                <FlashList
                  onScroll={handleScroll}
                  scrollEventThrottle={20}
                  ref={flashListRef}
                  data={teeSheetComponents}
                  showsVerticalScrollIndicator={false}
                  onViewableItemsChanged={onViewableItemsChanged}
                  ListHeaderComponent={
                    memberNote ? (
                      <Box
                        marginBottom={2}
                        justifyContent={"center"}
                        alignItems={"center"}
                        marginTop={!isMobileScreen ? 5 : 0}
                      >
                        <Box
                          maxWidth={isMobileScreen ? "100%" : 720}
                          width={"100%"}
                        >
                          <CourseNote
                            header={memberNote.header}
                            body={memberNote.body}
                            variant={isMobileScreen ? "underlay" : "inline"}
                            priority={memberNote.status}
                          />
                        </Box>
                      </Box>
                    ) : (
                      <Box height={28} mb={2}></Box>
                    )
                  }
                  keyExtractor={(item) => item.key}
                  ListFooterComponent={() => <Box height={96} />}
                  renderItem={({ item }) => (
                    <Box
                      px={{ base: 4, agendaSplit: 0 }}
                      maxWidth={{ base: "100%", md: 720 }}
                      width={"100%"}
                      style={{ marginHorizontal: "auto" }}
                    >
                      {item.component}
                    </Box>
                  )}
                />
              ))}
          </Box>
        </Box>
      </VStack>
    </>
  );
};

/* TODO: At a certain point we should be creating fragments from each main piece of the query.
 * Currently blockingRestrictions, infoRestrictions, requestRestrictions, and timeSlots are being used in the
 * useAgendaTeeSheetSlotsChangedSubscription and it would be better to have all of those as fragments we can pass in.
 */
export const TimeSlotListQueryString = graphql`
  query TimeSlotListInternalQuery(
    $facilityId: ID!
    $courseIds: [ID]
    $date: Date!
    $memberId: ID!
    $membershipTypeId: ID!
    $isCustomer: Boolean!
    $requestConfigs: Boolean!
  ) {
    session {
      currentClientProfile {
        member {
          club {
            id
            visibility
            supportsPublic
          }
        }
      }
    }
    node(id: $facilityId) {
      id
      __typename
      ... on Facility {
        ...getFacilityMemberPermissions_facility
        ...useGetCanClientFulFillRequests_facility
        courses {
          count
        }
        club {
          supportsPublic
        }
        type
        agendas(dateLte: $date, dateGte: $date) {
          edges {
            node {
              id
              date
              queueSlot {
                id
                queueCount: bookings {
                  count
                }
              }
              memberNote {
                id
                header
                body
                status
              }
              blockingRestrictions: bookingRules(
                type: [RESTRICTION]
                bookingType: [STANDARD]
                isBlocking: true
                courseIds: $courseIds
                memberVisibility: [
                  RESTRICTION_ONLY
                  SLOTS_AND_RESTRICTION
                  NONE
                ]
              ) {
                edges {
                  liftAt
                  node {
                    id
                    name
                    displayName
                    ... on BookingRestrictionRule {
                      memberMessage
                      visibilityColor
                      memberVisibility
                      liftBookingRuleWindow {
                        daysAhead
                        time
                      }
                      slotCriteria {
                        allDay
                        startAt
                        endAt
                        courses {
                          count
                          edges {
                            node {
                              id
                              name
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
              infoRestrictions: bookingRules(
                type: [RESTRICTION]
                isBlocking: false
                memberVisibility: [RESTRICTION_ONLY, SLOTS_AND_RESTRICTION]
                courseIds: $courseIds
              ) {
                edges {
                  liftAt
                  node {
                    id
                    name
                    displayName
                    ... on BookingRestrictionRule {
                      memberMessage
                      visibilityColor
                      memberVisibility
                      slotCriteria {
                        allDay
                        startAt
                        endAt
                        courses {
                          count
                          edges {
                            node {
                              name
                              id
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
              waitlistRestrictions: bookingRules(
                type: [WAITLIST]
                courseIds: $courseIds
              ) {
                edges {
                  liftAt
                  node {
                    id
                  }
                }
              }
              blocks(first: 1000, isVisibleToMembers: true) {
                edges {
                  node {
                    id
                    startAt
                    durationMins
                    blockSeries {
                      name
                      color
                    }
                    courses {
                      edges {
                        node {
                          id
                          name
                        }
                      }
                    }
                  }
                }
              }
              events(courseIds: $courseIds) {
                edges {
                  node {
                    id
                    eventSeries {
                      name
                    }
                    eventSessions(first: 1000) {
                      edges {
                        node {
                          id
                          startAt
                          endAt
                          courses {
                            edges {
                              node {
                                id
                                name
                              }
                            }
                          }
                          agenda {
                            date
                          }
                          name
                          timeSlots {
                            firstDateTime
                            lastDateTime
                          }
                          ...TimeListEventsCard_EventSession
                        }
                      }
                    }
                  }
                }
              }
              timeSlots(first: 1000, courseIds: $courseIds) {
                firstDateTime
                edges {
                  node {
                    ...TimeSlotRow_Timeslot
                      @arguments(membershipTypeId: $membershipTypeId)
                    id
                    time
                    dateTime
                    playerRateForMembershipType(
                      membershipTypeId: $membershipTypeId
                      type: CUSTOMER
                    ) {
                      rate {
                        id
                        name
                        type
                        code
                      }
                      nineHolePrice: totalGolfPrice(holeCount: NINE)
                      eighteenHolePrice: totalGolfPrice(holeCount: EIGHTEEN)
                    }
                    permittedCourseLayouts(first: 10, memberVisible: true) {
                      edges {
                        node {
                          id
                          name
                          holeCount
                        }
                      }
                    }
                    availability
                    capacity(bookingType: STANDARD)
                    usedCapacity
                    block {
                      id
                      startAt
                      durationMins
                      blockSeries {
                        isVisibleToMembers
                        name
                        color
                      }
                      courses {
                        edges {
                          node {
                            id
                          }
                        }
                      }
                    }
                    course {
                      id
                      name
                    }
                    eventSlot {
                      type
                      eventSession {
                        id
                      }
                    }
                    bookings {
                      edges {
                        node {
                          writeAccess
                          rounds(noShowStatus: [PRESENT]) {
                            edges {
                              node {
                                __typename
                                player {
                                  id
                                  firstName
                                  lastName
                                  __typename
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                    requestRestrictions: bookingRules(type: [REQUEST])
                      @skip(if: $requestConfigs) {
                      edges {
                        liftAt
                        node {
                          id
                          name
                          displayName
                          ... on RequestRule {
                            memberMessage
                            liftBookingRuleWindow {
                              daysAhead
                              time
                            }
                            slotCriteria {
                              allDay
                              startAt
                              endAt
                            }
                          }
                        }
                      }
                    }
                    configuredRequestRestrictions: requestWindowBookingRules(
                      type: [REQUEST]
                    ) @include(if: $requestConfigs) {
                      edges {
                        liftAt
                        requestOpens
                        requestCloses
                        node {
                          id
                          name
                          displayName
                          ... on RequestRule {
                            memberMessage
                            requestConfig {
                              closedRequestMessage
                              id
                            }
                            memberVisibility
                            visibilityColor
                            liftBookingRuleWindow {
                              daysAhead
                              time
                            }
                            slotCriteria {
                              allDay
                              startAt
                              endAt
                            }
                          }
                        }
                      }
                    }
                    hiddenBlockingRestrictions: bookingRules(
                      type: [RESTRICTION]
                      bookingType: [STANDARD]
                      isBlocking: true
                      memberVisibility: [RESTRICTION_ONLY, NONE]
                    ) {
                      edges {
                        liftAt
                      }
                    }
                    hasHiddenBlockingRestriction
                    waitlistRestrictions: bookingRules(type: [WAITLIST]) {
                      edges {
                        liftAt
                        node {
                          id
                        }
                      }
                    }
                  }
                }
              }
              requestRestrictions: bookingRules(type: [REQUEST])
                @skip(if: $requestConfigs) {
                edges {
                  liftAt
                  node {
                    id
                    name
                    displayName
                    ... on RequestRule {
                      memberMessage
                      liftBookingRuleWindow {
                        daysAhead
                        time
                      }
                      slotCriteria {
                        allDay
                        startAt
                        endAt
                        courses {
                          edges {
                            node {
                              id
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
              configuredRequestRestrictions: requestWindowBookingRules(
                type: [REQUEST]
                courseIds: $courseIds
              ) @include(if: $requestConfigs) {
                edges {
                  liftAt
                  requestOpens
                  requestCloses
                  node {
                    id
                    name
                    displayName
                    ... on RequestRule {
                      memberMessage
                      requestConfig {
                        closedRequestMessage
                        id
                      }
                      memberVisibility
                      visibilityColor
                      liftBookingRuleWindow {
                        daysAhead
                        time
                      }
                      slotCriteria {
                        allDay
                        startAt
                        endAt
                        courses {
                          edges {
                            node {
                              id
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
              weatherConditions(first: 1000) {
                edges {
                  node {
                    startTime
                    id
                    weatherCategory
                    probabilityOfPrecipitationInPercent
                    measurementSystem
                    temperature
                    humidityInPercent
                    windDegrees
                    windSpeedInMph
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
